import { ErrorMessage, NumberField } from '@doordash/component-fields'
import {
  Text,
  InlineChildren,
  Stack,
  StackChildren,
} from '@doordash/design-language'
import { has } from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import styled from 'styled-components'

import t from './strings'

const SpreadStack = styled(Stack)`
  display: flex;
  justify-content: space-between;
  padding: 0 4px;
`

const PaddedLabel = styled(Text)`
  padding: 4px 0 0 4px;
`

const SpreadInChildren = styled(InlineChildren)`
  justify-content: space-between;
`

// assumes date as a string 'YYYY-MM-DD'
const splitDate = date => date.split('-')

const getBirthDateArray = birthdayField => {
  const hasBirthdateValue =
    has(birthdayField, 'value') && birthdayField.value.length === 10
  return hasBirthdateValue ? splitDate(birthdayField.value) : ['', '', '']
}

type Props = {
  birthdayField: any
  id: string
  label: string
  minimumAge?: number
  onChange: (any) => void
  error?: string
  monthAnchorId?: string
  dayAnchorId?: string
  yearAnchorId?: string
}

type State = {
  currentYear: number
  currentMonth: number
  currentDay: number
  minBirthdate: any
  minYear: string
  minMonth: number
  minDay: number
  error: string
}

export class BirthdateInputSplit extends React.Component<Props, State> {
  public static defaultProps = {
    minimumAge: 18,
  }

  constructor(props) {
    super(props)

    const minBirthdate = moment().subtract(props.minimumAge, 'years')
    const [minYear, minMonth, minDay] = splitDate(
      minBirthdate.format('YYYY-MM-DD')
    )

    const [currentYear, currentMonth, currentDay] = getBirthDateArray(
      props.birthdayField
    )

    this.state = {
      currentYear,
      currentMonth,
      currentDay,
      minBirthdate,
      minYear,
      minMonth,
      minDay,
      error: '',
    }
  }

  public validateBirthdate = birthDateString => {
    const { minBirthdate } = this.state
    const birthDate = moment(birthDateString)

    if (birthDate.isValid() && birthDateString.length === 10) {
      if (birthDate.isBefore(minBirthdate)) {
        this.setState({ error: '' })
      } else {
        this.setState({ error: 'Not old enough' })
      }
    } else {
      this.setState({ error: '' })
    }
  }

  public handleChange = (field, value) => {
    const { onChange } = this.props

    this.setState({ [field]: value } as Pick<State, keyof State>)

    const { currentYear, currentMonth, currentDay } = {
      ...this.state,
      ...{ [field]: value },
    } as Pick<State, keyof State>

    const birthDateString = `${currentYear}-${currentMonth}-${currentDay}`

    this.validateBirthdate(birthDateString)

    if (onChange) {
      onChange(birthDateString)
    }
  }

  public render() {
    const {
      currentYear,
      currentMonth,
      currentDay,
      minDay,
      minMonth,
      minYear,
    } = this.state
    const {
      minimumAge,
      error,
      id,
      label,
      monthAnchorId,
      dayAnchorId,
      yearAnchorId,
    } = this.props

    const localError = error || null

    return (
      <div>
        <SpreadStack size={Stack.Sizes.XxxxSmall}>
          <Text tag="label" styles={Text.Styles.Medium14} htmlFor={id}>
            {label}
          </Text>
          <Text
            color={Text.Colors.TextTertiary}
            size={Text.Sizes.S12}
            weight={Text.Weights.Bold}
          >
            {t.required()}
          </Text>
        </SpreadStack>
        <SpreadInChildren id={id}>
          <StackChildren size={StackChildren.Sizes.XxxSmall}>
            <PaddedLabel
              size={Text.Sizes.Small}
              color={Text.Colors.Tertiary}
              htmlFor="month"
            >
              {t.month()}
            </PaddedLabel>
            <NumberField
              id="month"
              accessibilityDescribedBy={'birthdateMonth'}
              max={12}
              value={currentMonth}
              placeholder={minMonth}
              onChange={val => this.handleChange('currentMonth', val)}
              error={error && true}
              autoComplete="off"
              label={t.month()}
              data-anchor-id={monthAnchorId}
              isLabelHidden
            />
          </StackChildren>
          <StackChildren size={StackChildren.Sizes.XxxSmall}>
            <PaddedLabel
              size={Text.Sizes.Small}
              color={Text.Colors.Tertiary}
              htmlFor="day"
            >
              {t.day()}
            </PaddedLabel>
            <NumberField
              id="day"
              accessibilityDescribedBy={'birthdateDay'}
              max={31}
              value={currentDay}
              placeholder={minDay}
              onChange={val => this.handleChange('currentDay', val)}
              error={localError && true}
              autoComplete="off"
              label={t.day()}
              data-anchor-id={dayAnchorId}
              isLabelHidden
            />
          </StackChildren>
          <StackChildren size={StackChildren.Sizes.XxxSmall}>
            <PaddedLabel
              size={Text.Sizes.Small}
              color={Text.Colors.Tertiary}
              htmlFor="year"
            >
              {t.year()}
            </PaddedLabel>
            <NumberField
              id="year"
              accessibilityDescribedBy={'birthdateYear'}
              max={parseInt(minYear, 10) + minimumAge}
              value={currentYear}
              placeholder={minYear}
              onChange={val => this.handleChange('currentYear', val)}
              error={localError && true}
              autoComplete="off"
              label={t.year()}
              data-anchor-id={yearAnchorId}
              isLabelHidden
            />
          </StackChildren>
        </SpreadInChildren>
        {localError && <ErrorMessage error={localError || 'Required'} />}
      </div>
    )
  }
}

export default BirthdateInputSplit
